import React from 'react';
import Popper, { PopperProps } from '@mui/material/Popper';

/**
 * Dynamically gets the active CSS zoom factor from the root document element.
 * Defaults to 0.8 if zoom is applied, or 1 if not present.
 */
export const getZoomFactor = (): number => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 1;
  try {
    const docStyle = window.getComputedStyle(document.documentElement) as any;
    const zoom = parseFloat(docStyle?.zoom);
    if (zoom && !isNaN(zoom) && zoom > 0) {
      return zoom;
    }
  } catch (e) {
    // fallback if getComputedStyle fails
  }
  return 0.8;
};

/**
 * Popper modifier executed in 'beforeRead' phase to scale the reference element's
 * bounding rect from visual (screen) coordinates to layout coordinates.
 * Always reads from the live DOM element's getBoundingClientRect to ensure idempotency
 * and prevent coordinate accumulation during resize or zoom changes.
 */
export const zoomFixModifier = {
  name: 'zoomFix',
  enabled: true,
  phase: 'beforeRead' as const,
  fn: ({ state }: any) => {
    const zoom = getZoomFactor();
    if (zoom === 1) return;

    const referenceEl = state.elements?.reference;
    if (referenceEl && typeof referenceEl.getBoundingClientRect === 'function') {
      const rawRect = referenceEl.getBoundingClientRect();
      state.rects.reference = {
        x: rawRect.left / zoom,
        y: rawRect.top / zoom,
        width: rawRect.width / zoom,
        height: rawRect.height / zoom,
      };
    } else if (state.rects && state.rects.reference) {
      // Fallback if elements.reference is not available, using a unique tracking flag
      if (!state.rects.reference._zoomNormalized) {
        state.rects.reference.x /= zoom;
        state.rects.reference.y /= zoom;
        state.rects.reference.width /= zoom;
        state.rects.reference.height /= zoom;
        state.rects.reference._zoomNormalized = true;
      }
    }
  },
};

/**
 * Popper computeStyles modifier setting adaptive: false to ensure positions
 * are always anchored from top-left (0,0) with translate3d transform,
 * preventing bottom/right alignment bugs under CSS zoom.
 */
export const computeStylesModifier = {
  name: 'computeStyles',
  options: {
    adaptive: false,
    gpuAcceleration: true,
  },
};

/**
 * Wrapped Popper component that automatically injects zoom compensation modifiers
 * into popperOptions. Works seamlessly with MUI Tooltip, Autocomplete, and custom Poppers
 * even when custom PopperProps or disablePortal: true are passed.
 */
export const ZoomAdjustedPopper = React.forwardRef<HTMLDivElement, PopperProps>((props, ref) => {
  const { popperOptions, ...otherProps } = props;

  const mergedPopperOptions = React.useMemo(() => {
    const userModifiers = popperOptions?.modifiers || [];
    const hasComputeStyles = userModifiers.some((m: any) => m.name === 'computeStyles');

    const baseModifiers: any[] = [zoomFixModifier, hasComputeStyles ? null : computeStylesModifier].filter(Boolean);

    return {
      ...popperOptions,
      modifiers: [...baseModifiers, ...userModifiers],
    };
  }, [popperOptions]);

  return <Popper {...otherProps} popperOptions={mergedPopperOptions} ref={ref} />;
});

ZoomAdjustedPopper.displayName = 'ZoomAdjustedPopper';
